import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import RemoveIcon from "@mui/icons-material/Remove";
import axiosClient from "../../api/axiosClient";
import DataTable from "../brand/DataTable";
import { formatDateUk } from "../../utils/dateDisplay";

function PagedTable({ columns, rows, total, page, onPageChange, empty, renderRow }) {
  return (
    <>
      <DataTable size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column} align={column === "Action" ? "right" : "left"}>{column}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length}>{empty}</TableCell>
            </TableRow>
          )}
          {rows.map(renderRow)}
        </TableBody>
      </DataTable>
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={10}
        rowsPerPageOptions={[10]}
        onPageChange={(_, value) => onPageChange(value)}
      />
    </>
  );
}

export default function PropertyAccessTab({ propertyId, canEdit, onMessage, refreshKey = 0 }) {
  const [invites, setInvites] = useState([]);
  const [inviteTotal, setInviteTotal] = useState(0);
  const [invitePage, setInvitePage] = useState(0);
  const [access, setAccess] = useState([]);
  const [accessTotal, setAccessTotal] = useState(0);
  const [accessPage, setAccessPage] = useState(0);
  const [revoke, setRevoke] = useState(null);

  const loadInvites = useCallback(async (page) => {
    const response = await axiosClient.get(`/properties/${propertyId}/owner-invites/`, {
      params: { page: page + 1, pageSize: 10 },
    });
    setInvites(response.data.results || []);
    setInviteTotal(response.data.total || 0);
  }, [propertyId]);

  const loadAccess = useCallback(async (page) => {
    const response = await axiosClient.get(`/properties/${propertyId}/unit-access/`, {
      params: { page: page + 1, pageSize: 10 },
    });
    setAccess(response.data.results || []);
    setAccessTotal(response.data.total || 0);
  }, [propertyId]);

  useEffect(() => {
    loadInvites(invitePage).catch(() => onMessage?.("Could not load invites.", "error"));
  }, [loadInvites, invitePage, refreshKey, onMessage]);

  useEffect(() => {
    loadAccess(accessPage).catch(() => onMessage?.("Could not load unit access.", "error"));
  }, [loadAccess, accessPage, refreshKey, onMessage]);

  const resend = async (inviteId) => {
    await axiosClient.post(`/client-invites/${inviteId}/resend/`);
    onMessage?.("Invite resent.");
    loadInvites(invitePage);
  };

  const confirmRevoke = async () => {
    await axiosClient.post(`/users/${revoke.user_id}/client-revoke/`, { unit_id: revoke.unit_id });
    setRevoke(null);
    onMessage?.("Unit access removed.");
    loadAccess(accessPage);
  };

  return (
    <Stack spacing={3}>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Owner portal invites</Typography>
        <PagedTable
          columns={["Email", "Units", "Sent", "Action"]}
          rows={invites}
          total={inviteTotal}
          page={invitePage}
          onPageChange={setInvitePage}
          empty="No open invites."
          renderRow={(invite) => (
            <TableRow key={invite.id}>
              <TableCell>{invite.email}</TableCell>
              <TableCell>{(invite.units || []).join(", ")}</TableCell>
              <TableCell>{invite.created_at ? formatDateUk(invite.created_at) : "—"}</TableCell>
              <TableCell align="right">
                {canEdit && (
                  <Button variant="outlined" size="small" startIcon={<SendIcon />} onClick={() => resend(invite.id)}>
                    + Resend
                  </Button>
                )}
              </TableCell>
            </TableRow>
          )}
        />
      </Paper>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Unit access</Typography>
        <PagedTable
          columns={["Person", "Unit", "Action"]}
          rows={access}
          total={accessTotal}
          page={accessPage}
          onPageChange={setAccessPage}
          empty="No unit access."
          renderRow={(row) => (
            <TableRow key={`${row.user_id}-${row.unit_id}`}>
              <TableCell>
                <Link component={RouterLink} to={`/properties/${propertyId}/users/${row.user_id}`}>
                  {row.name || row.email}
                </Link>
              </TableCell>
              <TableCell>{row.unit_name}</TableCell>
              <TableCell align="right">
                {canEdit && (
                  <Button variant="outlined" color="error" size="small" startIcon={<RemoveIcon />} onClick={() => setRevoke(row)}>
                    Revoke
                  </Button>
                )}
              </TableCell>
            </TableRow>
          )}
        />
      </Paper>
      <Dialog open={Boolean(revoke)} onClose={() => setRevoke(null)}>
        <DialogTitle>Remove portal access?</DialogTitle>
        <DialogContent>
          <Typography>
            {revoke ? `${revoke.email} will no longer see ${revoke.unit_name}.` : ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoke(null)}>Cancel</Button>
          <Button variant="outlined" color="error" startIcon={<RemoveIcon />} onClick={confirmRevoke}>Revoke access</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
